import React, { useState, useRef } from "react";
import { db } from "../../../../config/firebase";
import { collection, doc, Timestamp, writeBatch } from "firebase/firestore";
import * as XLSX from "xlsx";
import moment from "moment";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Input as MuiInput,
  LinearProgress,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../../../../utils/exportTemplate";
import { normalizeSchoolYear } from "../../../../utils/schoolYearUtils";
import ModalDialogTitle from "../../../common/ModalDialogTitle";

const Upload = ({
  collectionName = "CreditCapture",
  onActionComplete,
  exportData,
  exportColumnOrder,
  templateFields,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentRecord, setCurrentRecord] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fileInputRef = useRef(null);

  const formatDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's already a Date object, return it
    if (dateValue instanceof Date) {
      return Timestamp.fromDate(dateValue);
    }

    // If it's a number (Excel date serial), convert it
    if (typeof dateValue === "number") {
      const excelEpoch = new Date(1900, 0, 1);
      const millisecondsInDay = 86400000;
      const adjustedDays = dateValue - 2;
      const date = new Date(
        excelEpoch.getTime() + adjustedDays * millisecondsInDay
      );

      const utcDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0
        )
      );

      return Timestamp.fromDate(utcDate);
    }

    // If it's a string, try parsing it
    if (typeof dateValue === "string") {
      if (dateValue.trim() === "") return null;

      const formats = [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
        "MM-DD-YYYY",
        "DD-MM-YYYY",
        "YYYY/MM/DD",
        "M/D/YYYY",
        "M-D-YYYY",
        "M/D/YY",
        "MM-DD-YY",
        "DD-MM-YY",
      ];

      let formattedDate = moment(dateValue, formats, true);

      if (!formattedDate.isValid()) {
        formattedDate = moment(dateValue);
      }

      if (formattedDate.isValid()) {
        formattedDate.utc().hour(12).minute(0).second(0).millisecond(0);
        return Timestamp.fromDate(formattedDate.toDate());
      }
    }

    console.warn("Failed to parse date value:", dateValue);
    return null;
  };

  const processFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          // Extract headers and data
          const headers = jsonData[0];
          const dataRows = jsonData
            .slice(1)
            .filter((row) =>
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== ""
              )
            );

          setTotalRecords(dataRows.length);
          setCurrentRecord(0);
          setUploadProgress(0);

          const collectionRef = collection(db, collectionName);
          const batchSize = 500; // Firestore batch limit
          let batchCount = 0;
          let batch = writeBatch(db);

          for (let index = 0; index < dataRows.length; index++) {
            const row = dataRows[index];
            const docData = {};

            // Process each field
            for (let i = 0; i < headers.length; i++) {
              const headerName = headers[i]?.toString().toLowerCase();
              let value = row[i];

              // Handle NULL, undefined, and empty values
              if (value === "NULL" || value === null || value === undefined) {
                value = "";
              }

              // Date field processing
              const dateHeaders = [
                "ctan_earned_date",
                "time_updated_stamp",
                "created_at",
                "date",
              ];

              if (
                headerName?.includes("date") ||
                dateHeaders.includes(headerName)
              ) {
                value = formatDate(value);
              }

              // Handle numeric fields
              if (headerName === "credits" || headerName === "tartan_id") {
                value = value ? Number(value) : null;
              }

              // School year is stored as "YYYY-YYYY" text. Number() turned
              // "2024-2025" into NaN, leaving uploaded records with no year;
              // a bare 2024 is converted to "2024-2025" instead.
              if (headerName === "course_school_year") {
                value = normalizeSchoolYear(value);
              }

              docData[headers[i]] = value;
            }

            // Add timestamp fields if not present
            if (!docData.CTAN_Earned_Date) {
              docData.CTAN_Earned_Date = Timestamp.now();
            }
            if (!docData.Time_Updated_Stamp) {
              docData.Time_Updated_Stamp = Timestamp.now();
            }

            // Create document reference and add to batch
            const newDocRef = doc(collectionRef);
            batch.set(newDocRef, docData);
            batchCount++;

            // Execute batch when it reaches the limit
            if (batchCount === batchSize || index === dataRows.length - 1) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }

            // Update progress
            const recordsCompleted = index + 1;
            setCurrentRecord(recordsCompleted);
            const progress = (recordsCompleted / dataRows.length) * 100;
            setUploadProgress(progress);

            // Small delay to allow UI updates
            if (index % 50 === 0) {
              await new Promise((resolve) => setTimeout(resolve, 10));
            }
          }

          resolve({ success: true, recordCount: dataRows.length });
        } catch (error) {
          console.error("Error processing file:", error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("File reading failed"));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleUpload = async () => {
    const fileInput = fileInputRef.current;
    const file = fileInput?.files[0];

    if (!file) {
      setSnackbar({
        open: true,
        message: "Please select a file to upload.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    setUploadDialogOpen(true);

    try {
      const result = await processFile(file);

      setSnackbar({
        open: true,
        message: `Successfully uploaded ${result.recordCount} records!`,
        severity: "success",
      });

      if (onActionComplete) {
        onActionComplete(
          true,
          `Successfully uploaded ${result.recordCount} records!`
        );
      }

      // Reset file input
      if (fileInput) {
        fileInput.value = "";
      }

      // Keep progress visible for 2 seconds then close dialog
      setTimeout(() => {
        setUploadDialogOpen(false);
        setUploadProgress(0);
        setCurrentRecord(0);
        setTotalRecords(0);
      }, 2000);
    } catch (error) {
      console.error("Upload failed:", error);
      setSnackbar({
        open: true,
        message: `Upload failed: ${error.message}`,
        severity: "error",
      });

      if (onActionComplete) {
        onActionComplete(false, `Upload failed: ${error.message}`);
      }

      setUploadDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!exportData || exportData.length === 0) {
      setSnackbar({
        open: true,
        message: "No data available for export.",
        severity: "warning",
      });
      return;
    }

    try {
      // Prepare data for export. Every field on the record is exported, so
      // anything SheetJS can't write as a plain cell must be converted first:
      // nested objects/arrays and invalid Dates produce malformed cells, and
      // control characters / overlong text are invalid in the sheet XML —
      // any of these makes Excel report the file as corrupt on open.
      const formatExportValue = (value) => {
        if (value === null || value === undefined) return "";
        // Handle Firestore Timestamps
        if (typeof value === "object" && typeof value.seconds === "number") {
          return new Date(value.seconds * 1000);
        }
        if (value instanceof Date) {
          return isNaN(value.getTime()) ? "" : value;
        }
        if (typeof value === "number") {
          return Number.isFinite(value) ? value : "";
        }
        if (typeof value === "boolean") return value;
        const text =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        return (
          text
            // eslint-disable-next-line no-control-regex
            .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
            .slice(0, 32767) // Excel's max characters per cell
        );
      };

      const dataToExport = exportData.map((item) => {
        const exportItem = {};
        const orderedColumns = Array.isArray(exportColumnOrder)
          ? exportColumnOrder
          : [];

        orderedColumns.forEach((column) => {
          exportItem[column] = formatExportValue(item[column]);
        });

        // Include every remaining field on the record, not just the
        // columns shown in the list view.
        Object.keys(item).forEach((key) => {
          if (orderedColumns.includes(key)) return;
          exportItem[key] = formatExportValue(item[key]);
        });

        return exportItem;
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "CreditCapture");

      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `credit-capture-export-${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(wb, filename);

      setSnackbar({
        open: true,
        message: "Data exported successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbar({
        open: true,
        message: "Export failed. Please try again.",
        severity: "error",
      });
    }
  };

  const handleExportTemplate = () => {
    const fields =
      templateFields && templateFields.length > 0
        ? templateFields
        : exportColumnOrder || [];
    if (fields.length === 0) {
      setSnackbar({
        open: true,
        message: "No fields configured for this template.",
        severity: "info",
      });
      return;
    }
    downloadExcelTemplate(fields, `${collectionName}-template`);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <MuiInput
        type="file"
        inputRef={fileInputRef}
        inputProps={{
          accept: ".xlsx,.xls,.csv",
        }}
        style={{ display: "none" }}
        id="upload-file-input"
      />
      <label htmlFor="upload-file-input">
        <Button
          component="span"
          variant="contained"
          disabled={loading}
          startIcon={<FontAwesomeIcon icon={faUpload} />}
        >
          Select File
        </Button>
      </label>

      <Button
        onClick={handleUpload}
        disabled={loading}
        variant="contained"
        color="secondary"
      >
        {loading ? <CircularProgress size={24} /> : "Upload Excel File"}
      </Button>

      <Button
        onClick={handleExport}
        disabled={loading}
        variant="outlined"
        startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
      >
        Export to Excel
      </Button>

      <Button
        onClick={handleExportTemplate}
        disabled={loading}
        variant="contained"
        color="success"
        startIcon={<FontAwesomeIcon icon={faFileExcel} />}
        sx={{ color: "white" }}
      >
        Export Template
      </Button>

      {/* Upload Progress Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <ModalDialogTitle onClose={() => setUploadDialogOpen(false)}>
          Upload Progress
        </ModalDialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Processing records: {currentRecord} of {totalRecords}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
              {Math.round(uploadProgress)}% Complete
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Upload;
